// Module 11 — the interactive control panel (NON-CONTRACT seam; behind frozen dashboards.v1's
// mountDashboards). Renders playback (play/pause + speed), per-layer on/off, and scenario-preset
// buttons against the narrow DOM seam, wired to the existing `Controls` (which drives the sim ONLY
// through the state store — never the engine). `sync(s)` reflects live store state onto the buttons
// (running label, active layer/speed highlight) so the panel mirrors the authoritative clock.
import type { Layer, Season } from "../domain/index";
import type { UiState } from "../state/index";
import type { UiDocument, UiElement, ScenarioCatalog } from "./seam";
import type { Controls } from "./controls";
import { tip, CONTROL_TIPS, anchorTip, presetTip } from "./help-registry";

const SPEEDS = [1, 2, 5, 10] as const;
const LAYERS: { key: Layer; label: string }[] = [
  { key: "energy", label: "Energy" },
  { key: "information", label: "Info" },
  { key: "finance", label: "Finance" },
];
const SEASONS: Season[] = ["winter", "spring", "summer", "fall"];

export interface ControlPanel {
  el: UiElement;
  sync(s: UiState): void;
}

function button(doc: UiDocument, label: string, className: string, onClick: () => void, tipText?: string): UiElement {
  const b = doc.createElement("button");
  b.className = className;
  b.textContent = label;
  b.addEventListener("click", onClick);
  if (tipText) tip(b, tipText); // Wave 2: hover/touch explanation on every control
  return b;
}

// Scenario presets as their OWN panel (split out of Controls so the preset list is fully visible and the
// Controls panel stays short). Pure DOM via the seam; clicks run the preset bundle through the store.
export function makeScenarioPanel(doc: UiDocument, controls: Controls, catalog: ScenarioCatalog): ControlPanel {
  const root = doc.createElement("div");
  root.className = "panel scenarios-panel";
  // ANCHOR PICKER (P14b): one click jumps the base scenario to a demand-distinct anchor (e.g.
  // winter-evening-peak −12°C vs spring-overnight-low +8°C). This is the most dramatic, instant proof the
  // sim responds — a different anchor re-shapes demand/dispatch/totals/map by thousands of MW immediately
  // (the pause-resolve applies it even while paused). Buttons highlight the live scenario in sync().
  const anchorLabel = doc.createElement("div");
  anchorLabel.className = "ctl-caption";
  anchorLabel.textContent = "Base scenario";
  const anchorBtns = catalog.anchors.map((a) => button(doc, a.label, "ctl-btn anchor", () => controls.setScenario(a.id), anchorTip(a.label)));
  const anchorRow = doc.createElement("div");
  anchorRow.className = "ctl-row anchors";
  anchorRow.append(...anchorBtns);
  const presetLabel = doc.createElement("div");
  presetLabel.className = "ctl-caption";
  presetLabel.textContent = "Event presets";
  const btns = catalog.presets.map((p) => button(doc, p.label, "ctl-btn preset", () => controls.runPreset(p.id), presetTip(p.label)));
  const row = doc.createElement("div");
  row.className = "ctl-row scenarios";
  row.append(...btns);
  root.append(anchorLabel, anchorRow, presetLabel, row);
  return {
    el: root,
    sync(s) {
      const id = s.latest?.scenario.id;
      for (let i = 0; i < catalog.anchors.length; i++) {
        anchorBtns[i]!.className = catalog.anchors[i]!.id === id ? "ctl-btn anchor active" : "ctl-btn anchor";
      }
    },
  };
}

export function makeControlPanel(
  doc: UiDocument, controls: Controls, catalog: ScenarioCatalog, opts?: { scenarios?: boolean },
): ControlPanel {
  const root = doc.createElement("div");
  root.className = "panel controls";

  // mirror of store state the click handlers read (a layer toggle flips its current visibility).
  let running = true;
  // Mirrors state/index.ts's seed (overwritten by sync() on the first real state, but the placeholder
  // should agree with the actual default rather than rely on sync() always running first).
  const layerVisible: Record<Layer, boolean> = { energy: true, information: true, finance: false };

  const playBtn = button(doc, "Pause", "ctl-btn play", () => (running ? controls.pause() : controls.play()), CONTROL_TIPS.play);
  const speedBtns = SPEEDS.map((sp) => button(doc, `${sp}x`, "ctl-btn speed", () => controls.setSpeed(sp), CONTROL_TIPS[`speed-${sp}`]));
  const playRow = doc.createElement("div");
  playRow.className = "ctl-row";
  playRow.append(playBtn, ...speedBtns);

  const layerBtns = LAYERS.map(({ key, label }) =>
    button(doc, label, "ctl-btn layer active", () => controls.setLayer(key, !layerVisible[key]), CONTROL_TIPS[`layer-${key}`]));
  // Phase 1.1: voltage overlay toggle — a viz-local coloring mode (node points by live pu), NOT a
  // simulation flow layer. Only shown when the composition root wired the callback.
  let voltageOn = false;
  const voltageBtn = button(doc, "Voltage", "ctl-btn layer", () => {
    if (!controls.setVoltageOverlay) return;
    voltageOn = !voltageOn;
    controls.setVoltageOverlay(voltageOn);
    (voltageBtn as unknown as { classList?: { toggle(cls: string, force: boolean): void } }).classList?.toggle("active", voltageOn);
  }, CONTROL_TIPS["layer-voltage"]);
  const layerRow = doc.createElement("div");
  layerRow.className = "ctl-row";
  layerRow.append(...layerBtns, voltageBtn);

  const presetBtns = catalog.presets.map((p) =>
    button(doc, p.label, "ctl-btn preset", () => controls.runPreset(p.id), presetTip(p.label)));
  const scenarioRow = doc.createElement("div");
  scenarioRow.className = "ctl-row scenarios";
  scenarioRow.append(...presetBtns);

  // ── v3 environment controls: hour + temperature sliders, season + day-type buttons ──
  const slider = (cls: string, min: number, max: number, onChange: (v: number) => void, tipText?: string): UiElement => {
    const s = doc.createElement("input");
    s.className = cls;
    s.setAttribute?.("type", "range");
    s.setAttribute?.("min", String(min));
    s.setAttribute?.("max", String(max));
    s.setAttribute?.("step", "1");
    if (tipText) tip(s, tipText); // Wave 2
    const fire = (): void => {
      const v = Number(s.value);
      if (Number.isFinite(v)) onChange(v);
    };
    s.addEventListener("input", fire); // LIVE drag — fires on each step so the value tracks the user's finger
    s.addEventListener("change", fire); // release (belt-and-suspenders; some environments only emit one)
    return s;
  };
  const hourLabel = doc.createElement("span");
  hourLabel.className = "env-label hour";
  const tempLabel = doc.createElement("span");
  tempLabel.className = "env-label temp";
  // optimistic label: update the readout from the drag value IMMEDIATELY (don't wait for the snapshot
  // round-trip, which never arrives while paused) so the control visibly confirms the user's input.
  const setHourLabel = (h: number): void => { hourLabel.textContent = `hour ${String(((h % 24) + 24) % 24).padStart(2, "0")}:00`; };
  const setTempLabel = (t: number): void => { tempLabel.textContent = `${t > 0 ? "+" : ""}${Math.round(t)}°C`; };
  const hourSlider = slider("env-slider hour", 0, 23, (v) => { setHourLabel(v); controls.setHour(v); }, CONTROL_TIPS["slider-hour"]);
  const tempSlider = slider("env-slider temp", -25, 40, (v) => { setTempLabel(v); controls.setTempC(v); }, CONTROL_TIPS["slider-temp"]);
  // live "demand now" badge (P14b): the headline number the hour/temperature/season controls move, shown
  // right next to the sliders so a change is unmistakable (with the pause-resolve it updates even paused).
  const demandBadge = doc.createElement("div");
  demandBadge.className = "env-demand";
  // Keep each label glued to ITS slider in a non-breaking pair, so when the narrow Controls panel wraps the
  // row the temperature value stays IN FRONT of the temperature slider (it used to wrap off to the hour line).
  const hourPair = doc.createElement("div");
  hourPair.className = "env-pair";
  hourPair.append(hourLabel, hourSlider);
  const tempPair = doc.createElement("div");
  tempPair.className = "env-pair";
  tempPair.append(tempLabel, tempSlider);
  const envRow = doc.createElement("div");
  envRow.className = "ctl-row env";
  envRow.append(hourPair, tempPair, demandBadge);

  const seasonBtns = SEASONS.map((season) =>
    button(doc, season, "ctl-btn season", () => controls.setSeason(season), CONTROL_TIPS[`season-${season}`]));
  const dayBtns = (["weekday", "weekend"] as const).map((d) =>
    button(doc, d, "ctl-btn daytype", () => controls.setDayType(d), CONTROL_TIPS[`day-${d}`]));
  const seasonRow = doc.createElement("div");
  seasonRow.className = "ctl-row seasons";
  seasonRow.append(...seasonBtns, ...dayBtns);

  // scenarios live in their OWN panel by default (opts.scenarios !== false keeps them here for the
  // legacy single-panel callers + the control-panel test).
  if (opts?.scenarios === false) root.append(playRow, layerRow, envRow, seasonRow);
  else root.append(playRow, layerRow, envRow, seasonRow, scenarioRow);

  return {
    el: root,
    sync(s) {
      running = s.running;
      playBtn.textContent = s.running ? "Pause" : "Play";
      playBtn.className = s.running ? "ctl-btn play running" : "ctl-btn play";
      for (let i = 0; i < LAYERS.length; i++) {
        const vis = s.layersVisible[LAYERS[i]!.key];
        layerVisible[LAYERS[i]!.key] = vis;
        layerBtns[i]!.className = vis ? "ctl-btn layer active" : "ctl-btn layer";
      }
      for (let i = 0; i < SPEEDS.length; i++) {
        speedBtns[i]!.className = s.clock.speed === SPEEDS[i] ? "ctl-btn speed active" : "ctl-btn speed";
      }
      // env controls mirror the LIVE working scenario from the latest snapshot
      const sc = s.latest?.scenario;
      if (s.latest) {
        demandBadge.textContent = `demand ${Math.round(s.latest.totals.grossDemandMw).toLocaleString()} MW · λ $${Math.round(s.latest.totals.marginalCostPerMwh ?? 0)}`;
      }
      if (sc) {
        hourLabel.textContent = `hour ${String(sc.hour).padStart(2, "0")}:00`;
        hourSlider.value = String(sc.hour);
        tempLabel.textContent = `${sc.tempC > 0 ? "+" : ""}${Math.round(sc.tempC)}°C`;
        tempSlider.value = String(Math.round(sc.tempC));
        for (let i = 0; i < SEASONS.length; i++) {
          seasonBtns[i]!.className = sc.season === SEASONS[i] ? "ctl-btn season active" : "ctl-btn season";
        }
        dayBtns[0]!.className = sc.dayType === "weekday" ? "ctl-btn daytype active" : "ctl-btn daytype";
        dayBtns[1]!.className = sc.dayType === "weekend" ? "ctl-btn daytype active" : "ctl-btn daytype";
      }
    },
  };
}
